// Account deletion. Pure w.r.t. React — throws on failure (the caller resets
// UI state only after this resolves), never touches component state itself.
import { getDocs, deleteDoc, writeBatch } from "firebase/firestore";
import { db, userCol, userMeta, LS } from "./firebase";
import { ONBOARDED_KEY, clearLocalCache } from "./sync";

// Reauthenticate FIRST (a stale token fails both the Firestore rules and
// user.delete()), THEN wipe, THEN delete the auth account. If reauth is
// cancelled nothing is touched. Throws on failure — SettingsPage toasts it.
export async function deleteAccount(user) {
  if (!user) throw new Error("You're not signed in.");
  const uid = user.uid;
  const provider = user.providerData?.[0]?.providerId;
  const isNative = window.Capacitor?.isNativePlatform?.();
  const A = await import("firebase/auth");

  // 1. Re-authenticate. MUST succeed before we touch any data — a provider with
  // no reauth path here can't be deleted safely (data-gone / account-alive).
  if (provider === "google.com") {
    if (isNative) {
      const { FirebaseAuthentication } = await import("@capacitor-firebase/authentication");
      const r = await FirebaseAuthentication.signInWithGoogle();
      const idToken = r.credential?.idToken;
      if (!idToken) throw new Error("Couldn't confirm your Google sign-in. Please try again.");
      await A.reauthenticateWithCredential(user, A.GoogleAuthProvider.credential(idToken));
    } else {
      await A.reauthenticateWithPopup(user, new A.GoogleAuthProvider());
    }
  } else if (provider === "password") {
    const pw = window.prompt("Enter your password to permanently delete your account:");
    if (!pw) throw Object.assign(new Error("Deletion cancelled."), { cancelled: true });
    await A.reauthenticateWithCredential(user, A.EmailAuthProvider.credential(user.email, pw));
  } else {
    throw new Error("Account deletion isn't available for this sign-in method yet. Please contact support.");
  }

  // 2. Wipe Firestore (batched — a slow serial delete can outlast the reauth
  // window) + local caches. Token is fresh from step 1.
  for (const col of ["clients", "payments", "appointments", "attendance", "batches", "leads"]) {
    const snap = await getDocs(userCol(uid, col));
    for (let i = 0; i < snap.docs.length; i += 450) {
      const batch = writeBatch(db);
      snap.docs.slice(i, i + 450).forEach(d => batch.delete(d.ref));
      await batch.commit();
    }
  }
  await deleteDoc(userMeta(uid));
  clearLocalCache(uid);
  const ob = LS.get(ONBOARDED_KEY);
  if (Array.isArray(ob)) LS.set(ONBOARDED_KEY, ob.filter(u => u !== uid));

  // 3. Delete the auth account.
  await user.delete();
}
